import React, { useState } from 'react';
import api from '../../axios'; // axios instance with JWT setup
import '../css/DatasetSubmissionForm.css';

const DATASET_TYPES = [
  { value: 'genome', label: 'Genome Assembly' },
  { value: 'gene', label: 'Gene or Transcript' },
  { value: 'sra', label: 'SRA / Raw Data' },
  { value: 'literature', label: 'Literature / Publication' },
  { value: 'protein', label: 'Protein Sequence' },
  { value: 'nucleotide', label: 'Nucleotide Sequence' },
  { value: 'other', label: 'Other' },
];

const DatasetSubmissionForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dataset_type: 'gene',
    submitter_name: '',
    email: '',
    institution: '',
    metadata_json: '{}', // must be sent to backend
    uploaded_file: null,
  });

  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const validate = () => {
    const errs = {};
    if (!formData.title.trim()) errs.title = 'Title is required.';
    if (!formData.description.trim()) errs.description = 'Description is required.';
    if (!formData.submitter_name.trim()) errs.submitter_name = 'Submitter name is required.';
    if (!formData.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errs.email = 'Invalid email format.';
    if (!formData.uploaded_file) errs.uploaded_file = 'Dataset file is required.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setMessage({ type: '', text: '' });

    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      setMessage({ type: 'error', text: 'Please fix the errors above.' });
      return;
    }

    setLoading(true);

    try {
      const payload = new FormData();
      payload.append('title', formData.title);
      payload.append('description', formData.description);
      payload.append('dataset_type', formData.dataset_type);
      payload.append('submitter_name', formData.submitter_name);
      payload.append('email', formData.email);
      payload.append('institution', formData.institution || '');
      payload.append('metadata_json', formData.metadata_json);
      payload.append('uploaded_file', formData.uploaded_file);

      await api.post('/dataset-submissions/', payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setMessage({ type: 'success', text: 'Dataset submitted successfully!' });
      setFormData({
        title: '',
        description: '',
        dataset_type: 'gene',
        submitter_name: '',
        email: '',
        institution: '',
        metadata_json: '{}',
        uploaded_file: null,
      });

      if (onSuccess) onSuccess();
    } catch (error) {
      const errMsg =
        error.response?.data?.detail ||
        JSON.stringify(error.response?.data) ||
        'Submission failed. Please try again.';
      setMessage({ type: 'error', text: errMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="dataset-form" noValidate>
      <h2>Dataset Submission</h2>

      {message.text && (
        <div className={message.type === 'success' ? 'message-success' : 'message-error'}>
          {message.text}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="title">Title *</label>
        <input
          id="title"
          name="title"
          type="text"
          value={formData.title}
          onChange={handleChange}
          className={errors.title ? 'input-error' : ''}
          placeholder="Enter dataset title"
        />
        {errors.title && <p className="error-text">{errors.title}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="description">Description *</label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          className={errors.description ? 'input-error' : ''}
          placeholder="Describe your dataset"
          rows={4}
        />
        {errors.description && <p className="error-text">{errors.description}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="dataset_type">Dataset Type *</label>
        <select
          id="dataset_type"
          name="dataset_type"
          value={formData.dataset_type}
          onChange={handleChange}
        >
          {DATASET_TYPES.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label htmlFor="submitter_name">Submitter Name *</label>
        <input
          id="submitter_name"
          name="submitter_name"
          type="text"
          value={formData.submitter_name}
          onChange={handleChange}
          className={errors.submitter_name ? 'input-error' : ''}
          placeholder="Your full name"
        />
        {errors.submitter_name && <p className="error-text">{errors.submitter_name}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="email">Email *</label>
        <input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          className={errors.email ? 'input-error' : ''}
          placeholder="your@email.com"
        />
        {errors.email && <p className="error-text">{errors.email}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="institution">Institution</label>
        <input
          id="institution"
          name="institution"
          type="text"
          value={formData.institution}
          onChange={handleChange}
          placeholder="Institution name"
        />
      </div>

      <div className="form-group">
        <label htmlFor="uploaded_file">Upload Dataset File *</label>
        <input
          id="uploaded_file"
          name="uploaded_file"
          type="file"
          onChange={handleChange}
          className={errors.uploaded_file ? 'input-error' : ''}
        />
        {errors.uploaded_file && <p className="error-text">{errors.uploaded_file}</p>}
      </div>

      <button type="submit" disabled={loading} className="btn-primary">
        {loading ? 'Submitting...' : 'Submit Dataset'}
      </button>
    </form>
  );
};

export default DatasetSubmissionForm;

